/**
 * detection-odors-edit.js
 */

import React from 'react';
import { FormControl, FormHelperText } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useFormContext, useForm, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import get from 'lodash.get';
import { Types } from 'utils/constants';
import { shouldRenderMobileLayout } from 'utils/app-context';
import { sleep } from 'utils/misc';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import FormSectionTitle from 'components/menu-drawer/form-section-title';
import HiddenInput from 'components/form-inputs/hidden-input';
import { DetectionOdorsContext } from '../context/detection-odors-context';
import { EnvironmentSection, EnvironmentExpander } from './detection-environment';
import { 
  defaultEnvironment, 
  getEnvironmentIds, 
  getOdorIds, 
  odorlessEnvironmentExists, 
  updateDefaultToCurrent 
} from './detection-common';


const useStyles = makeStyles(theme => ({
  expandersContainer: {
    marginTop: shouldRenderMobileLayout() ? theme.spacing(1) : 'inherit',
    marginLeft: window.innerWidth > 450 ? theme.spacing(2) : 0,
    '& > *:not(:first-child)': {
      marginTop: -2,
    },
    '& > .environment:not(:first-child)': {
      marginTop: 50,
    },
  },
  title: {
    [theme.breakpoints.down(theme.size.menuDrawer.typeBreakpointWidth)]: {
      display: 'none'
    }
  }
}));


export default function DetectionOdorsEdit(props) {
  const {
    name,
    recordType,
    drawerState,
    translation,
    options,
    disabled,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation(translation);
  const { setExpandedId } = React.useContext(DetectionOdorsContext);
  const formContext = useFormContext();
  const { control, formState, getValues, setValue, clearErrors } = formContext;
  const { fields: detectionEnvironments, append, remove } = useFieldArray({
    control,
    name,
    keyName: "hookFormId1"
  });
  const environmentIds = useWatch({ control, name: 'detectionEnvironmentIds' });
  const recordId = useWatch({ control, name: 'id' });
  const workModeValue = useWatch({ control, name: 'workMode' });
  const shapeErrorRef = React.useRef();

  // create a separate set of hook form methods for controlling
  // the add item forms because we don't want them in the actual
  // arrays until they have been added.
  // kinda annoying that there isn't a better way - that i know of...
  const addingFormContext = useForm();


  // reset meta structures if record changes
  React.useEffect(() => {
    setExpandedId();

    // update meta lists
    setTimeout(() => {
      const structure = get(getValues(), name);
      setValue('detectionEnvironmentIds', getEnvironmentIds(structure));
      setValue('detectionOdorlessEnvironmentExists', odorlessEnvironmentExists(structure));
      setValue('detectionOdorIds', getOdorIds(structure));
    }, 100);
  }, [recordId, name, setExpandedId, getValues, setValue]);


  // add environment
  const doAdd = async () => {
    drawerState.loading.content.set(true);

    const newEnvTemplate = get(addingFormContext.getValues(), `${name}[0]`);
    const {odorEnvironments: _, ...newEnv} = newEnvTemplate;

    setExpandedId();
    await sleep(0);
    append(newEnv);
    await sleep(100);

    clearErrors('detectionEnvironmentsShape');

    // update cached environments list
    const structure = get(getValues(), name);
    setValue('detectionEnvironmentIds', getEnvironmentIds(structure));
    
    // update default values with newly-added environment
    const formNamePrefix = `${name}[${detectionEnvironments?.length ?? 0}]`;
    updateDefaultToCurrent(formContext, formNamePrefix, newEnv);
    
    drawerState.loading.content.set(false);
  }
  

  // remove environment
  const doRemove = (index) => {
    remove(index);

    // update meta lists
    setTimeout(() => {
      const structure = get(getValues(), name);
      setValue('detectionEnvironmentIds', getEnvironmentIds(structure), { shouldDirty: true });
      setValue('detectionOdorlessEnvironmentExists', odorlessEnvironmentExists(structure), { shouldDirty: true });
      setValue('detectionOdorIds', getOdorIds(structure), { shouldDirty: true });
    });
  }


  // validate structure - only used for exers, one environment is required.
  const validateDetectionEnvironments = (message) => {
    return workModeValue !== Types.WorkMode.DETECTION || (environmentIds && environmentIds.length > 0) || message;
  }


  return (
    <>
      {/* element ref used to scroll to position on shape error */}
      <div ref={shapeErrorRef} style={{ position: 'relative', top: -60 }}></div>

      {/* title */}
      <HelpTextItem
        sidebarText={drawerState.sidebarText}
        title={t('environments.general.helpTitle')}
        text={t('environments.general.helpInfo')}
        className={classes.title}
      >
        <FormSectionTitle>{t('environments.sectionName')}</FormSectionTitle>
      </HelpTextItem>

      {/* environments */}
      <div className={classes.expandersContainer}>
        {detectionEnvironments?.filter(env => env.hookFormId1)?.map((environment, envIndex) =>
          <EnvironmentSection
            key={environment.hookFormId1}
            recordType={recordType}
            baseName={name}
            envIndex={envIndex}
            environment={environment}
            onDelete={index => doRemove(index)}
            options={options}
            formContext={formContext}
            addingFormContext={addingFormContext}
            disabled={disabled}
            drawerState={drawerState}
            translation={translation}
          />)}
        {/* add environment */}
        <EnvironmentExpander
          id='new-env'
          recordType={recordType}
          baseName={name}
          isNew={true}
          envIndex={0}
          environment={defaultEnvironment(options)}
          onAdd={doAdd}
          formContext={addingFormContext}
          mainControl={control}
          options={options}
          disabled={disabled}
          drawerState={drawerState}
          translation={translation}
          forceError={Boolean(formState.errors?.detectionEnvironmentsShape)}
        />

        {/*
          front-end-only lists of environments & odors used to prevent
          heavy processing of nested objects resulting in multiple renders.
          they are calculated and imperatively set manually via setValue
          on appropriate changes instead of responding to any change in the
          structure which caused horrible performance.

          TODO: consider moving into detection odors context.
        */}
        <HiddenInput
          name='detectionEnvironmentIds'
          defaultValue={[]}
        />
        <HiddenInput
          name='detectionOdorlessEnvironmentExists'
          defaultValue={null}
        />
        <HiddenInput
          name='detectionOdorIds'
          defaultValue={[]}
        />

        {/* hidden unused field used for detection shape validation */}
        {recordType === Types.Record.EXERCISE && 
        <FormControl error={Boolean(formState.errors?.detectionEnvironmentsShape)}>
          <HiddenInput
            name='detectionEnvironmentsShape'
            defaultValue=""
            rules={{
              validate: () => validateDetectionEnvironments(t('environments.general.errMsgEmpty'))
            }}
            errorRef={shapeErrorRef}
          />
          {Boolean(formState.errors?.detectionEnvironmentsShape) &&
          <FormHelperText style={{ marginLeft: 16 }}>
            {formState.errors.detectionEnvironmentsShape.message}
          </FormHelperText>}
        </FormControl>}

      </div>

    </>
  );
}